import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Tag,
  ModalContent,
  CModalHeader,
  ModalCloseButton,
  CModalBody,
  CModalFooter,
  VStack,
  Flex,
  useToast,
  Text,
} from '@televet/kibble-ui';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { email as emailRegex } from 'shared/utils/validation';
import { useSendWidgetCodeToDeveloperMutation } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsRequestWidgetEvents } from 'shared/providers/TrackingProvider/EventNames';

interface ISendToDeveloperForm {
  email: string;
  cc?: string;
  message?: string;
}

interface ISendToDeveloperModalContent {
  onClose: () => void;
}

const SendToDeveloperModalContent = ({ onClose }: ISendToDeveloperModalContent): JSX.Element => {
  const { clinicUser } = useClinicUser();
  const toast = useToast({ position: 'top', variant: 'solid' });
  const { errors, register, handleSubmit, getValues } = useForm<ISendToDeveloperForm>({
    validateCriteriaMode: 'all',
    reValidateMode: 'onChange',
    defaultValues: { cc: clinicUser?.email || '' },
  });
  const [isEditingCc, setIsEditingCc] = useState(false);
  const { trackEvent } = useTracking();

  const [sendWidgetCodeToDeveloper, { loading: isSendingWidgetCodeToDeveloper }] = useSendWidgetCodeToDeveloperMutation(
    {
      onCompleted: () => {
        toast({ description: 'Email sent' });
        onClose();
      },
      onError: () => {
        toast({
          status: 'error',
          description: 'Whoops! Something went wrong while sending the email. Please try again.',
        });
      },
    },
  );

  const onFormSubmit = useCallback(
    (values: ISendToDeveloperForm) => {
      if (isSendingWidgetCodeToDeveloper) {
        return;
      }
      const { email, cc, message } = values;

      trackEvent(SettingsRequestWidgetEvents.SendCodeToDev);
      sendWidgetCodeToDeveloper({
        variables: { data: { emailAddress: email, ccEmailAddresses: cc || '', message } },
      });
    },
    [isSendingWidgetCodeToDeveloper, sendWidgetCodeToDeveloper, trackEvent],
  );

  return (
    <ModalContent>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <CModalHeader fontSize="3xl" pb="1">
          Send to Web Developer
        </CModalHeader>
        <ModalCloseButton />
        <CModalBody>
          <Text>
            We&apos;ll add instructions to an email about how to add the script to your website. Feel free to provide a
            message for your web developer below to let them know what&apos;s going on.
          </Text>
          <VStack mt="6" spacing="4" align="flex-start">
            <TextInput
              name="email"
              autoFocus
              label="Developer's Email"
              isRequired
              labelStyle={{ fontWeight: 'semibold' }}
              isInvalid={!!errors.email?.type}
              errorText="Please enter a valid email."
              placeholder="developer@email.com"
              ref={register({ required: 'Please enter a valid email.', pattern: emailRegex })}
            />
            {isEditingCc ? (
              <Textarea
                autoFocus
                label="Cc"
                labelStyle={{ fontWeight: 'semibold' }}
                helperText="Separate multiple emails with a comma."
                name="cc"
                isInvalid={!!errors.cc?.type}
                errorText={errors.cc?.message?.toString() || 'Please separate valid emails with a comma.'}
                ref={register({
                  validate: (value: string) => {
                    const isValid = !value.trim() || value.split(',').every((email) => emailRegex.test(email.trim()));
                    return isValid || 'Please separate valid emails with a comma.';
                  },
                })}
              />
            ) : (
              <>
                <Flex alignItems="baseline">
                  <Text variant="subtle">Cc: </Text>
                  <Tag label={getValues('cc') || ''} bg="background.subtle" ml="2" mr="1" />
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={(): void => {
                      setIsEditingCc(true);
                    }}
                  >
                    Edit
                  </Button>
                </Flex>
                <TextInput type="hidden" defaultValue={getValues('cc')} name="cc" ref={register} />
              </>
            )}
            <Textarea
              name="message"
              label="Message"
              labelStyle={{ fontWeight: 'semibold' }}
              placeholder="Please include this code on my website."
              ref={register}
            />
          </VStack>
        </CModalBody>

        <CModalFooter justifyContent="space-between">
          <Button size="md" variant="tertiary" bg="#EBEEEF" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="md" isLoading={isSendingWidgetCodeToDeveloper}>
            Send Email
          </Button>
        </CModalFooter>
      </form>
    </ModalContent>
  );
};
export default SendToDeveloperModalContent;
